<script setup lang="ts">
import { useRepairsContext } from '@/composables/rc/drawer-context'
import { usePartDetails } from '@/composables/rc/part-details'
import { usePartReplacements } from '@/composables/rc/part-replacements'
import type { IComponent } from '@/types/rc/miners'
import { Icon } from '@iconify/vue'
import { ref, watch } from 'vue'

defineProps<{
  partVersion?: string
}>()

const { minerId, selectedPart } = useRepairsContext()
const { partId, partComponents, isReplacement, partModel, partModelVersion, movedToInventory } = usePartDetails(
  minerId,
  selectedPart
)

// UI state

const addingComponent = ref<boolean>(false)
const selectedComponentType = ref<string | undefined>()
const selectedComponent = ref<IComponent | undefined>()

const resetAddingComponent = () => {
  addingComponent.value = false
  selectedComponentType.value = undefined
  selectedComponent.value = undefined
}

watch(selectedPart, resetAddingComponent)

// Fetch component types for selected part (static for the prop lifecycle)
const { componentTypes, componentsFor, addComponentsMutation, deleteComponentMutation } = usePartReplacements(
  minerId,
  selectedPart,
  partModel,
  partModelVersion
)

// Enabled and immediately mounted
const { data: componentTypesData } = componentTypes(addingComponent)

// Fetch components for selected part type - Disabled until `selectedComponentType` is defined
const { data: avaliableComponentsForPart, isFetching: fetchingComponents } = componentsFor(selectedComponentType)

// Event handlers

const onChangeComponentType = () => {
  if (!selectedComponentType.value) return
  selectedComponent.value = undefined
}

const onChangeComponent = () => {
  if (!partId.value || !selectedComponent.value) return
  addComponentsMutation.mutate(
    {
      part_id: partId.value,
      components: [...(partComponents.value ?? []), selectedComponent.value],
    },
    {
      onSettled: resetAddingComponent,
    }
  )
}

const onDeleteComponent = (component: IComponent) => {
  if (!partId.value) return
  deleteComponentMutation.mutate(
    {
      part_id: partId.value,
      component_id: component.id,
    },
    {
      onSettled: resetAddingComponent,
    }
  )
}
</script>

<template>
  <div class="flex flex-col gap-sm w-full">
    <el-text class="text-text-tertiary w-full">Used components</el-text>

    <!-- Display table -->
    <div v-if="partComponents && partComponents.length > 0">
      <el-table :data="partComponents" stripe style="width: 100%" :show-header="false">
        <el-table-column label="Component">
          <template #default="scope">
            <span>{{ scope.row.type }} - {{ scope.row.display_name }}</span>
          </template>
        </el-table-column>
        <el-table-column width="64">
          <template #default="scope">
            <el-button v-if="!movedToInventory" link @click="onDeleteComponent(scope.row as IComponent)">
              <Icon class="text-text-tertiary hover:text-red" icon="material-symbols:delete-rounded" height="20" />
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- Select inputs -->
    <div v-if="addingComponent" class="grid grid-cols-[1fr_1fr_16px] gap-spacing-xs">
      <el-text class="text-text-tertiary">Type</el-text>
      <el-text class="text-text-tertiary">Component</el-text>

      <!-- Grid padding div -->
      <div />

      <!-- Type dropdown -->
      <el-select v-model="selectedComponentType" filterable @change="onChangeComponentType">
        <template #empty><span class="px-2">No comp. types available for this model</span></template>
        <el-option
          v-for="componentType in componentTypesData"
          :key="componentType.name"
          :value="componentType.name"
          :label="componentType.display_name"
        />
      </el-select>

      <!-- Actual component dropdown -->
      <el-select
        v-model="selectedComponent"
        filterable
        :loading="fetchingComponents"
        value-key="pcb_id"
        @change="onChangeComponent"
      >
        <template #empty><span class="px-2">No comp. types available for this type</span></template>
        <el-option
          v-for="component in avaliableComponentsForPart"
          :key="component.display_name"
          :value="component"
          :label="component.display_name"
        />
      </el-select>

      <Icon
        v-if="addingComponent"
        class="text-text-secondary hover:text-red cursor-pointer self-center"
        icon="material-symbols:close-rounded"
        :height="16"
        @click="resetAddingComponent"
      />
    </div>

    <!-- "Add component" text button -->
    <el-tooltip content="Not available until part model is set" :disabled="!!partModel">
      <el-link
        v-if="!addingComponent"
        class="self-start"
        type="primary"
        :disabled="!partModel || isReplacement || movedToInventory"
        @click="addingComponent = true"
        ><Icon icon="mdi:plus" height="20" /> Add used components</el-link
      >
    </el-tooltip>
  </div>
</template>
